import React, { useEffect, useState } from 'react'
import { formatDriverName } from '../../utils/driverName'
import ModalLayout from '../shared/ModalLayout'
import AddAnotherDayActivityFooter from './AddAnotherDayActivitFooter'
import AddAnotherDayActivityHeader from './AddAnotherDayActivityHeader'
import { FieldsetSelect } from '@/modules/settings/components/FieldsetSelect'
import { useDayEvents } from '../../hooks/useDayEvents'
import { DateField } from '@/ui/form/controls/DateField'
import useDriverService from '../../services/DriverService'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  useLoadingStore,
  selectLoading,
} from '@/modules/shared/stores/loadingStore'

type DriverOption = { id: number; name: string }

const AddAnotherDayActivityModal = ({
  close,
  companyId,
  driverId,
  selectedDates,
  onInserted,
}: {
  close: () => void
  companyId: string
  driverId: number
  selectedDates: string[]
  onInserted?: () => void
}) => {
  const { t } = useLocalize()
  const { handleInsertActivityFromAnotherDay } = useDayEvents()
  const { getDrivers, getDriver } = useDriverService()
  const [drivers, setDrivers] = useState<DriverOption[] | null>(null)
  const [driverName, setDriverName] = useState('')
  const [sourceDriverId, setSourceDriverId] = useState<number | null>(null)
  const [sourceDate, setSourceDate] = useState('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const requestPending = useLoadingStore(selectLoading)

  const targetDates = React.useMemo(
    () => Array.from(new Set(selectedDates)).sort(),
    [selectedDates],
  )

  React.useEffect(() => {
    document.body.setAttribute('data-modal-open', 'true')

    return () => {
      document.body.removeAttribute('data-modal-open')
    }
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      setIsError(false)
      try {
        const data = await getDrivers({ companyId, onlyActive: true })
        const options: DriverOption[] = (data ?? []).map((d) => ({
          id: d.id,
          name: d.fullName || formatDriverName(d),
        }))
        setDrivers(options)
        const current = options.find((d) => Number(d.id) === Number(driverId))
        if (current) {
          setSourceDriverId(current.id)
          setDriverName(current.name)
        } else {
          const driver = await getDriver({
            companyId,
            driverId,
            withPersonalData: false,
          })
          setDriverName(driver?.fullName || formatDriverName(driver ?? {}))
        }
      } catch (error) {
        setIsError(true)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    setErrorMessage('')
  }, [sourceDate, sourceDriverId])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (isSubmitting) return
    const sourceDriver = drivers?.find((d) => d.id === sourceDriverId)
    if (!sourceDriver || !sourceDate || targetDates.length === 0) {
      setErrorMessage(t('driverModule.all-fields-required'))
      return
    }

    setIsSubmitting(true)
    const inserted = await handleInsertActivityFromAnotherDay({
      companyId,
      driverId,
      driverName,
      dates: targetDates,
      sourceDriverId: sourceDriver.id,
      sourceDriverName: sourceDriver.name,
      sourceDate,
    })
    if (inserted) {
      close()
      onInserted?.()
    } else {
      setIsSubmitting(false)
    }
  }

  const showSpinner = isLoading || (isSubmitting && requestPending)

  return (
    <ModalLayout customZIndex={250}>
      <form
        onSubmit={handleSubmit}
        noValidate
        className="tw-relative tw-z-[10000] tw-flex tw-max-h-[90vh] tw-w-full tw-max-w-[450px] tw-flex-col tw-rounded-lg tw-bg-white tw-shadow-2xl"
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        {showSpinner && (
          <div className="tw-absolute tw-inset-0 tw-z-[10001] tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-black tw-bg-opacity-50">
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-rounded-lg tw-bg-white tw-p-8 tw-shadow-xl">
              <div className="tw-h-12 tw-w-12 tw-animate-spin tw-rounded-full tw-border-4 tw-border-gray-200 tw-border-t-primary"></div>
              <p className="tw-text-lg tw-font-medium tw-text-gray-700">
                {isLoading
                  ? t('driverModule.loading-drivers')
                  : t('common.loading')}
              </p>
            </div>
          </div>
        )}

        <AddAnotherDayActivityHeader
          close={() => {
            if (!isSubmitting) close()
          }}
        />
        <div className="tw-space-y-4 tw-p-6">
          <div className="tw-relative tw-w-full">
            <FieldsetSelect
              className=""
              required
              label={t('driverModule.select-driver')}
              placeholder={
                !drivers || drivers.length === 0
                  ? t('common.loading')
                  : t('driverModule.select-driver')
              }
              value={sourceDriverId?.toString() || ''}
              name="driver"
              disabled={!drivers || drivers.length === 0 || isLoading}
              onChange={(e) => {
                const id = Number(e.target.value)
                if (drivers?.some((d) => d.id === id)) {
                  setSourceDriverId(id)
                }
              }}
              options={
                !drivers || drivers.length === 0
                  ? [{ label: t('common.loading'), value: '' }]
                  : drivers.map((driver) => ({
                      label: driver.name,
                      value: driver.id.toString(),
                    }))
              }
            />
          </div>
          <div className="tw-relative">
            <DateField
              required
              label={t('common.select-date')}
              value={sourceDate}
              setValue={(e) => setSourceDate(e)}
              helper={t('driverModule.copy-activities-date-helper')}
            />
          </div>
          <div>
            <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-content-base">
              {t('driverCalendar.insert-activities-target-days')} (
              {targetDates.length})
            </p>
            <p className="tw-m-0 tw-max-h-24 tw-overflow-y-auto tw-text-sm tw-text-content-base/70">
              {targetDates.join(', ')}
            </p>
          </div>
          {errorMessage && (
            <p className="tw-mt-1 tw-text-sm tw-text-red-500">{errorMessage}</p>
          )}

          {isError && (
            <p className="tw-mt-1 tw-text-sm tw-text-red-500">
              {t('common.error')}
            </p>
          )}
        </div>
        <AddAnotherDayActivityFooter />
      </form>
    </ModalLayout>
  )
}

export default AddAnotherDayActivityModal
